import React, { Suspense } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Briefcase, BrainCircuit, Headphones, Workflow, Code2, CalendarRange, Boxes, Globe2, ShieldCheck, Activity, Server, Rocket } from "lucide-react";
import Hero3D from "../components/Hero3D";
import SectionLabel from "../components/SectionLabel";

const SERVICES = [
  { icon: Briefcase, title: "BPO Solutions", points: ["End-to-end back-office support", "High-speed transaction processing", "Scalable non-voice operations"] },
  { icon: BrainCircuit, title: "KPO Insights", points: ["Specialized technical research", "Financial analytics & reporting", "Strategic market intelligence"] },
  { icon: Headphones, title: "Customer Support", points: ["24/7 Voice & Non-Voice support", "Omnichannel delivery excellence", "Global CX performance teams"] },
  { icon: Workflow, title: "Automation & Workflow", points: ["Intelligent process transformation", "Custom RPA implementations", "Workflow bottleneck optimization"] },
  { icon: Code2, title: "Web & App Dev", points: ["Enterprise platform architecture", "Custom mobile application tools", "Scalable full-stack solutions"] },
  { icon: CalendarRange, title: "Event Digital Ops", points: ["End-to-end event infrastructure", "Real-time broadcast tech support", "Global digital management"] },
  { icon: Boxes, title: "Digital Products", points: ["Product lifecycle management", "Go-to-market strategy support", "Value-driven growth optimization"] },
];

const WHY = [
  { icon: Globe2, title: "Global service quality", points: ["Secure enterprise-grade infrastructure", "Rigorous multi-tier QA processes", "Performance-centric delivery models"] },
  { icon: ShieldCheck, title: "Tech-integrated operations", points: ["AI-enhanced automation workflows", "Proprietary tech stack integration", "Real-time data & analytics visibility"] },
  { icon: Activity, title: "Cost-effective India delivery", points: ["High-tier talent at competitive scale", "Follow-the-sun 24/7 global support", "Lean operations with premium output"] },
  { icon: Server, title: "End-to-end solutions", points: ["Strategic consulting and design", "Full-lifecycle managed services", "Agile transition and scaling"] },
];

const INDUSTRIES = [
  ["SaaS & Technology", "Cloud-native operational support and digital lifecycle management."],
  ["FinTech", "Secure back-office and customer operations for financial systems."],
  ["E-commerce & D2C", "End-to-end supply chain visibility and omnichannel support."],
  ["Healthcare Support", "Secure data management and patient workflow optimization."],
  ["Startups & SMBs", "Agile scaling solutions tailored for rapid growth cycles."],
  ["Events & Culture", "Digital infrastructure and real-time operations for global events."],
  ["IT Services", "Technical helpdesk and automated operational efficiency."],
  ["Global Enterprises", "Enterprise-grade outsourcing integrated with legacy systems."],
];

const STEPS = [
  { num: "01", title: "Discovery & Strategy", points: ["Audit workflows for automation", "Identify cost efficiencies", "Define iterative ROI roadmaps"] },
  { num: "02", title: "Solution Architecture", points: ["Engineer AI-human hybrid models", "Map definitive success KPIs", "Architecture for global security"] },
  { num: "03", title: "Global Implementation", points: ["Mobilize global delivery hubs", "Maintain 99.9% continuity", "Deploy enterprise-grade workflows"] },
  { num: "04", title: "Scaling & Optimization", points: ["Real-time performance tracking", "Refine processes for ROI gains", "Dynamic scaling for growth"] },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.6, ease: "easeOut" } }),
};

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-[#050505] text-white">
      {/* HERO */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden">
        <Suspense fallback={null}>
          <Hero3D />
        </Suspense>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black pointer-events-none" />
        <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={0}>
              <SectionLabel>Operexa Gennex Private Limited</SectionLabel>
            </motion.div>
            <motion.h1
              initial="hidden" animate="visible" variants={fadeUp} custom={1}
              className="font-display font-bold text-4xl sm:text-5xl lg:text-7xl tracking-tighter mt-6 leading-[1.05]"
            >
              Smart Operations.<br />
              <span className="text-[#3B82F6]">Scalable</span> Growth.
            </motion.h1>
            <motion.p
              initial="hidden" animate="visible" variants={fadeUp} custom={2}
              className="mt-6 text-zinc-300 text-base lg:text-lg max-w-2xl leading-relaxed"
            >
              Enterprise-grade outsourcing, automation, and digital solutions built for fast-growing global businesses. We engineer the operational backbone that lets your business scale with confidence.
            </motion.p>

            <motion.ul
              initial="hidden" animate="visible" variants={fadeUp} custom={3}
              className="mt-8 grid sm:grid-cols-2 gap-3 max-w-2xl"
            >
              {[
                "Boost operational efficiency with AI-driven workflows",
                "Scale rapidly using global talent hubs and flexible delivery",
                "Drive profitability through cost-optimized offshore ecosystems",
              ].map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-zinc-300">
                  <span className="block w-1.5 h-1.5 mt-2 bg-[#3B82F6]" />
                  {p}
                </li>
              ))}
            </motion.ul>

            <motion.div
              initial="hidden" animate="visible" variants={fadeUp} custom={4}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-talk"
                className="group inline-flex items-center gap-2 px-6 py-3 bg-[#3B82F6] text-white font-semibold hover:bg-[#60A5FA] transition-colors"
              >
                Talk to Us
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/services"
                data-testid="hero-cta-services"
                className="group inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white font-semibold hover:border-white hover:bg-white/5 transition-colors"
              >
                Explore Services
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial="hidden" animate="visible" variants={fadeUp} custom={5}
            className="lg:col-span-4 hidden lg:block"
          >
            <div className="relative border border-white/10 bg-white/[0.02] backdrop-blur-md p-6">
              <div className="font-mono text-[10px] tracking-[0.25em] text-[#3B82F6] uppercase mb-4">
                Live Operational Index
              </div>
              <div className="space-y-5">
                {[
                  { k: "Process accuracy", v: "99.9%" },
                  { k: "Avg. response time", v: "< 4h" },
                  { k: "Cost reduction", v: "30–40%" },
                  { k: "Global coverage", v: "24 / 7" },
                ].map((row) => (
                  <div key={row.k} className="flex items-baseline justify-between border-b border-white/5 pb-2.5">
                    <span className="text-xs text-zinc-400 uppercase tracking-wider">{row.k}</span>
                    <span className="font-display text-2xl text-white tabular-nums">{row.v}</span>
                  </div>
                ))}
              </div>
              <div className="absolute -bottom-3 -right-3 w-20 h-20 border border-[#3B82F6]/40 -z-10" />
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase">
          Scroll
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="relative py-24 lg:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl">
            <SectionLabel>What We Do</SectionLabel>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight mt-5">
              Engineered for Performance.<br />
              <span className="text-zinc-500">Governed for Scale.</span>
            </h2>
            <p className="mt-5 text-zinc-400 max-w-2xl">
              Enterprise-grade outsourcing, automation, and digital solutions designed to accelerate growth for fast-moving global businesses.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-white/5 border border-white/10">
            {SERVICES.map((s, i) => {
              const Ic = s.icon;
              return (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-[#050505] p-7 hover:bg-[#0d0d10] transition-colors group"
                  data-testid={`service-card-${i}`}
                >
                  <div className="w-10 h-10 border border-white/15 flex items-center justify-center mb-5 group-hover:border-[#3B82F6] group-hover:bg-[#3B82F6]/10 transition-colors">
                    <Ic className="w-5 h-5 text-[#3B82F6]" />
                  </div>
                  <h3 className="font-display text-xl font-semibold mb-3">{s.title}</h3>
                  <ul className="space-y-2 text-sm text-zinc-400">
                    {s.points.map((p) => (
                      <li key={p} className="flex items-start gap-2">
                        <span className="block w-1 h-1 mt-2 bg-zinc-600" />
                        {p}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY OPEREXA */}
      <section className="relative py-24 lg:py-32 border-t border-white/10 bg-[#08080a]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <SectionLabel>Why Operexa Gennex</SectionLabel>
              <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight mt-5">
                The operational backbone for global scale.
              </h2>
              <p className="mt-5 text-zinc-400">
                We blend enterprise governance with startup agility. The result: measurable, predictable outcomes you can scale on.
              </p>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/10">
              {WHY.map((w, i) => {
                const Ic = w.icon;
                return (
                  <div key={w.title} className="bg-[#08080a] p-7" data-testid={`why-card-${i}`}>
                    <Ic className="w-6 h-6 text-[#3B82F6] mb-4" />
                    <h3 className="font-display text-lg font-semibold mb-3">{w.title}</h3>
                    <ul className="space-y-2 text-sm text-zinc-400">
                      {w.points.map((p) => <li key={p} className="flex items-start gap-2"><span className="w-1 h-1 mt-2 bg-zinc-600 block" />{p}</li>)}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="relative py-24 lg:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div className="max-w-2xl">
              <SectionLabel>Industries We Serve</SectionLabel>
              <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight mt-5">
                High-growth sectors. Mission-critical workflows.
              </h2>
            </div>
            <Link to="/industries" data-testid="industries-explore" className="inline-flex items-center gap-2 text-sm font-medium text-white border-b border-white/30 pb-1 hover:border-white">
              Explore All Industries <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/10">
            {INDUSTRIES.map(([title, desc], i) => (
              <div key={title} data-testid={`industry-card-${i}`} className="bg-[#050505] p-6 hover:bg-[#0d0d10] transition-colors">
                <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 uppercase mb-3">0{i + 1}</div>
                <h3 className="font-display text-base font-semibold mb-2">{title}</h3>
                <p className="text-xs text-zinc-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE ENGAGE */}
      <section className="relative py-24 lg:py-32 border-t border-white/10 bg-[#08080a]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-2xl mb-16">
            <SectionLabel>How We Engage</SectionLabel>
            <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight mt-5">
              A four-step path to operational excellence.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 border border-white/10">
            {STEPS.map((s, i) => (
              <div key={s.num} data-testid={`step-${i}`} className="bg-[#08080a] p-7 relative">
                <div className="font-mono text-[10px] tracking-[0.25em] text-[#3B82F6] uppercase mb-4">Step {s.num}</div>
                <h3 className="font-display text-xl font-semibold mb-4">{s.title}</h3>
                <ul className="space-y-2 text-sm text-zinc-400">
                  {s.points.map((p) => <li key={p} className="flex items-start gap-2"><span className="w-1 h-1 mt-2 bg-zinc-600 block" />{p}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="relative bg-[#0a0a0c] border border-white/10 p-10 lg:p-16 overflow-hidden">
            <div className="absolute -right-20 -top-20 w-72 h-72 border border-[#3B82F6]/20 rounded-full" />
            <div className="absolute -right-32 -top-32 w-96 h-96 border border-[#3B82F6]/10 rounded-full" />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <SectionLabel>Next Steps</SectionLabel>
                <h2 className="font-display text-3xl lg:text-5xl font-semibold tracking-tight mt-4">
                  Ready to scale operations with confidence?
                </h2>
                <p className="mt-4 text-zinc-400 max-w-2xl">
                  Our specialized teams integrate enterprise-grade authority with startup agility to optimize your workflows. Build your sustainable growth engine today.
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <Link to="/contact" data-testid="bottom-cta" className="inline-flex items-center gap-2 px-6 py-3 bg-[#3B82F6] text-white font-semibold hover:bg-[#60A5FA] transition-colors">
                  Talk to the Operexa Team <Rocket className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
